import * as React from 'react';

/**
 * Свёрнутость разделов бокового меню (`SidebarGroup`). Настройка интерфейса,
 * а не состояние экрана — как и `wit-admin:sidebar-collapsed`, поэтому в
 * `PERSISTED_SCREEN_STATE_KEYS` ключ не входит и при выходе не стирается.
 *
 * Формат — JSON `Record<groupKey, boolean>`. Хранятся только свёрнутые группы:
 * раскрытие удаляет запись, ведь раскрыто всё, чего пользователь сам не
 * свернул.
 */
export const SIDEBAR_GROUPS_KEY = 'wit-admin:sidebar-groups';

type GroupsState = Record<string, boolean>;

/**
 * Запасная копия на случай недоступного хранилища (приватный режим, запрет
 * сайта): переключатель всё равно работает до перезагрузки страницы.
 */
let memoryRaw: string | null = null;
/**
 * Последняя запись в хранилище не удалась (квота, Safari private: `getItem`
 * работает, `setItem` бросает). Тогда в хранилище лежит устаревшее значение,
 * и верить ему нельзя — источником правды становится `memoryRaw`, пока
 * очередная запись не пройдёт.
 */
let lastWriteFailed = false;
const listeners = new Set<() => void>();

function readRaw(): string | null {
  if (lastWriteFailed) return memoryRaw;
  try {
    return window.localStorage.getItem(SIDEBAR_GROUPS_KEY);
  } catch {
    return memoryRaw;
  }
}

function parse(raw: string | null): GroupsState {
  if (raw === null) return {};
  try {
    const value: unknown = JSON.parse(raw);
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
    const out: GroupsState = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (typeof v === 'boolean') out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/** Текущее состояние групп. Мусор в хранилище или недоступное хранилище → `{}`. */
export function readSidebarGroups(): GroupsState {
  return parse(readRaw());
}

function emit(): void {
  for (const listener of listeners) listener();
}

/** Раскрыта ли группа по сохранённому состоянию. Нет записи → раскрыта. */
export function isSidebarGroupExpanded(key: string): boolean {
  return readSidebarGroups()[key] !== false;
}

/** Записывает состояние группы и оповещает все подписанные экземпляры меню. */
export function setSidebarGroupExpanded(key: string, expanded: boolean): void {
  const next = readSidebarGroups();
  if (expanded) delete next[key];
  else next[key] = false;
  const raw = Object.keys(next).length === 0 ? null : JSON.stringify(next);
  memoryRaw = raw;
  try {
    if (raw === null) window.localStorage.removeItem(SIDEBAR_GROUPS_KEY);
    else window.localStorage.setItem(SIDEBAR_GROUPS_KEY, raw);
    lastWriteFailed = false;
  } catch {
    // Best-effort — недоступное хранилище не должно ломать навигацию.
    lastWriteFailed = true;
  }
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Другая вкладка поменяла настройку — подхватываем (`key === null` — clear()).
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === SIDEBAR_GROUPS_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Состояние групп для компонента. Одно внешнее хранилище на всё приложение
 * (`useSyncExternalStore`), поэтому десктопный сайдбар и мобильная `Sheet`
 * всегда показывают одно и то же, без правок оболочки.
 */
export function useSidebarGroups(): {
  isExpanded(key: string): boolean;
  setExpanded(key: string, expanded: boolean): void;
} {
  // Снимок — сырая строка: сравнивается по значению, лишних рендеров нет.
  const raw = React.useSyncExternalStore(subscribe, readRaw, () => null);
  const state = React.useMemo(() => parse(raw), [raw]);
  return React.useMemo(
    () => ({
      isExpanded: (key: string) => state[key] !== false,
      setExpanded: setSidebarGroupExpanded,
    }),
    [state],
  );
}
